"use client";

import { Link } from "@/i18n/navigation";
import { Download, Gamepad2, Send, Trophy } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import { isTauri } from "@/shared/lib/tauri";
import { SHOW_TAURI_FEATURES } from "@/shared/config";
import {
  Alert,
  AlertTitle,
  AlertDescription,
} from "@/shared/ui/feedback/alert";
import { Button } from "@/shared/ui/button";

interface DownloadAppBannerProps {
  className?: string;
  variant?: "default" | "match-critical";
}

export function DownloadAppBanner({
  className,
  variant = "default",
}: DownloadAppBannerProps) {
  const tUi = useTranslations("Ui");
  const t = useTranslations("Match.noTauri");
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = () => {
      setIsMobile(window.innerWidth < 768);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // Десктоп-фичи временно скрыты (KAN-82): баннер «Скачать приложение» — только под флагом.
  if (!SHOW_TAURI_FEATURES) return null;
  if (isTauri || isMobile) return null;

  if (variant === "match-critical") {
    return (
      <Alert
        className={`border-destructive/30 bg-destructive/5 ${className ?? ""}`.trim()}
      >
        <Download className="h-4 w-4" />
        <AlertTitle className="text-base">{t("title")}</AlertTitle>
        <AlertDescription className="flex flex-col gap-3">
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <Gamepad2 className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{t("bullet_autoLobby")}</span>
            </li>
            <li className="flex items-start gap-2">
              <Send className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{t("bullet_autoInvite")}</span>
            </li>
            <li className="flex items-start gap-2">
              <Trophy className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{t("bullet_autoResult")}</span>
            </li>
          </ul>
          <Button size="default" className="w-fit" asChild>
            <Link href="/download">{t("cta")}</Link>
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert className={className}>
      <Download className="h-4 w-4" />
      <AlertTitle>{tUi("downloadAppBanner.avtomaticheskoeOtslezhivanieMatchey")}</AlertTitle>
      <AlertDescription className="flex flex-col gap-2">
        <span>
          {tUi("downloadAppBanner.skachaytePrilozhenieGoodluckDlya")}
        </span>
        <Button variant="outline" size="sm" className="w-fit" asChild>
          <Link href="/download">{tUi("downloadAppBanner.skachatPrilozhenie")}</Link>
        </Button>
      </AlertDescription>
    </Alert>
  );
}
